import React, { useState } from "react";
import {
    AppstoreOutlined,
    BulbOutlined,
    CompassOutlined,
    HomeOutlined,
    SettingOutlined,
    UsergroupAddOutlined,
} from "@ant-design/icons";
import { Menu } from "antd";
import { Link } from "react-router-dom";
import styled from "styled-components";
import theme from "../../themes";
import withPermissions from "../../hoc/withPermissions";
import adminMenuItems from "./adminMenuItems";
import { hasPermission } from "../../helpers/HasPermission";

const MenuWrapper = styled.div`
    width: ${props => (props.isCollapsed ? "90px" : "256px")};
    transition: width 0.2s;
    position: fixed;
    z-index: 50;
    top: 0;
    bottom: 0;

    .ant-menu {
        padding: 0 16px !important;
    }

    .ant-menu,
    .ant-menu-submenu > .ant-menu {
        background: #f5f5f5 !important;
    }

    .ant-menu-submenu-title,
    .ant-menu-item {
        display: flex;
        align-items: center;
    }

    .ant-menu-submenu-title .anticon,
    .ant-menu-item .anticon {
        font-size: 24px !important;
        line-height: 0 !important;
    }

    .ant-menu-inline-collapsed > .ant-menu-item {
        padding: 0 2px !important;
    }

    .ant-menu-item {
        padding: 0 2px !important;
        margin: 32px 10px !important;

        a {
            display: flex;
            align-items: center;
            color: ${props => props.theme.darkGrey};

            &:hover {
                color: ${props => props.theme.primaryColor};
            }
        }
    }

    .ant-menu-inline-collapsed > .ant-menu-submenu > .ant-menu-submenu-title {
        padding: 0 8px !important;
    }

    .ant-menu-submenu > .ant-menu-submenu-title {
        padding: 0 8px !important;
        margin: 32px 4px 8px 4px !important;
    }

    .ant-menu-submenu-title,
    .ant-menu-sub .ant-menu-item {
        margin: 8px 4px !important;
    }

    .ant-menu-inline-collapsed > .menu-item-logo {
        padding: 0 !important;
    }

    .menu-item-logo {
        width: 100%;
        padding: 0 !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
        height: 47px !important;
    }

    .menu-item-logo img {
        display: block;
    }

    .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {
        background: none;
        &:after {
            display: none;
        }

        > a {
            color: ${props => props.theme.primaryColor};
        }
    }

    a {
        color: ${props => props.theme.darkGrey};
    }
`;

const LogoWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;
const Logo = styled.div`
    margin: 0px;
    display: block;
`;
const MenuName = styled.span`
    color: ${props => props.theme.darkGrey};
    margin-left: 2px;
    margin-top: ${props => (props.noSpace ? "2px" : "4px")};

    &:hover {
        color: ${props => props.theme.darkGrey};
    }
`;

const VerticalNav = ({ activeMenuItem, permissions }) => {
    const [isCollapsed, setIsCollapsed] = useState(true);

    const getMenuItems = () => {
        const items = [
            {
                key: "logo",
                className: "menu-item-logo",
                label: (
                    <Link to={"/"}>
                        <LogoWrapper>
                            <Logo isCollapsed={isCollapsed}>
                                <img
                                    alt="Logo"
                                    style={{
                                        width: "auto",
                                        height: "47px",
                                    }}
                                    src={
                                        isCollapsed
                                            ? theme.logo_square || theme.logo
                                            : theme.logo
                                    }
                                />
                            </Logo>
                        </LogoWrapper>
                    </Link>
                ),
            },
            {
                type: "divider",
            },
            {
                key: "home",
                className: "menu-item-home",
                label: (
                    <Link to={"/"}>
                        <HomeOutlined />
                        <MenuName>Home</MenuName>
                    </Link>
                ),
            },
            {
                key: "challenges",
                className: "menu-item-challenge",
                label: (
                    <Link to={"/challenges"}>
                        <CompassOutlined />
                        <MenuName>Challenges</MenuName>
                    </Link>
                ),
            },
            {
                key: "ideas",
                className: "menu-item-ideas",
                label: (
                    <Link to={"/ideas"}>
                        <BulbOutlined />
                        <MenuName>Ideas</MenuName>
                    </Link>
                ),
            },
            // Keep this on staging only for now.
            // {
            //   key: "submissions",
            //   className: "menu-item-submissions",
            //   label: (
            //     <Link to={"/submissions"}>
            //       <AppstoreOutlined />
            //       <MenuName>Submissions</MenuName>
            //     </Link>
            //   ),
            // },
        ];

        // Add divider if user has admin permissions
        if (
            hasPermission(permissions, "administer users") ||
            hasPermission(permissions, "administer task_workflow")
        ) {
            items.push({
                type: "divider",
            });
        }

        // Add user management item if user has permission
        if (hasPermission(permissions, "administer users")) {
            items.push({
                key: "user_management",
                className: "menu-item-user_management",
                label: (
                    <Link to={"/admin/user-management"}>
                        <UsergroupAddOutlined />
                        <MenuName>Team Members</MenuName>
                    </Link>
                ),
            });
        }

        // Add admin submenu if user has permission
        if (hasPermission(permissions, "administer task_workflow")) {
            items.push({
                key: "admin",
                className: "menu-item-admin admin-menu",
                label: (
                    <>
                        <SettingOutlined />
                        <MenuName noSpace>Settings</MenuName>
                    </>
                ),
                children: adminMenuItems.map(item => ({
                    key: item.key,
                    className: `menu-item-${item.key}`,
                    label: (
                        <Link className="nav-link" to={item.path}>
                            {item.text}
                        </Link>
                    ),
                })),
            });
        }

        return items;
    };

    return (
        <MenuWrapper isCollapsed={isCollapsed}>
            <Menu
                className="hide-link-styles"
                onMouseOver={() => setIsCollapsed(false)}
                onMouseLeave={() => setIsCollapsed(true)}
                // onClick={() => setIsCollapsed(c => !c)}
                selectedKeys={[activeMenuItem]}
                defaultOpenKeys={[activeMenuItem]}
                mode="inline"
                theme="light"
                inlineCollapsed={isCollapsed}
                items={getMenuItems()}
                style={{
                    height: "100%",
                    overflowY: "auto",
                    borderRight: "1px solid #ddd",
                }}
            />
        </MenuWrapper>
    );
};

export default withPermissions(VerticalNav);
